'use client'

import clsx from 'clsx'
import { t as lt } from '@lingui/core/macro'
import { Search } from 'react-feather'
import HelpIcon from '@mui/icons-material/Help'

import logoGif from '@/assets/img/logo.gif'
import { LanguageSwitcher } from '@/shared/ui/LanguageSwitcher'
import { MeetingLinksAutocomplete } from '@/features/autocomplete'
import { AccountMenu } from '@/features/account-menu'
import { getEnv } from '@/lib/env'
import { Image } from '@/lib/next-shim'
import { Link } from '@/lib/i18n/settings'
import { usePathname } from '@/lib/i18n/settings'
import { useIsFR } from '@/hooks/useIsFR'
import { shouldUseCustomBranding } from '@/utils/landing-variant'

import Notification from './Notification'

type HeaderProps = {
  searchBar: () => void
  menu: Array<{
    icon: React.ReactNode
    label: string
    name: string
    path: string
  }>
}

export function Header({ menu, searchBar }: HeaderProps) {
  const pathname = usePathname()
  const isFR = useIsFR()

  // Get branding information for white labeling
  const isCustomBranded = shouldUseCustomBranding()
  const brandLogo = getEnv('NEXT_PUBLIC_BRAND_LOGO', '')
  const brandName = getEnv('NEXT_PUBLIC_BRAND_NAME', 'Medconnect Platform')

  return (
    <header role="banner">
      <div className="fixed w-full bg-white px-2 sm:px-4 lg:px-6 shadow z-[1100]">
        <div className="flex h-16 items-center justify-between">
          <Link className="flex items-center justify-center" href="/">
            {isCustomBranded && brandLogo ? (
              <Image alt={brandName} src={brandLogo} width={107} height={60} className="h-16 w-auto object-contain" />
            ) : (
              <Image alt="logo" src={logoGif} width={107} height={60} className="h-16 w-auto" />
            )}
          </Link>
          <div className="hidden md:ml-6 md:flex md:justify-center">
            <nav aria-label="Main navigation" className="flex text-sm font-bold leading-6 text-gray-700">
              <ul className="flex space-x-4 list-none m-0 p-0">
                {menu.map((item, itemIdx) => (
                  <li key={itemIdx}>
                    <Link
                      className={clsx('inline-flex items-center border-b-2 border-primary px-1 pt-1 text-sm font-medium text-primary', {
                        'border-transparent hover:border-gray-300 hover:!text-gray-700 !text-gray-500': !pathname?.startsWith(item.path),
                      })}
                      href={item.path}
                      aria-current={pathname?.startsWith(item.path) ? 'page' : undefined}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="flex items-center">
            <div className="hidden lg:block lg:w-[22rem] xl:w-[30rem] 2xl:w-[34rem] mx-3">
              <MeetingLinksAutocomplete type="header" />
            </div>
            <button
              type="button"
              aria-label="Search"
              className="bg-white flex lg:hidden text-gray-500 mx-1 p-1 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              onClick={searchBar}
            >
              <Search size={22} aria-hidden="true" />
            </button>
            {isFR && (
              <Link
                href="/account/ethics"
                className={clsx(
                  'flex items-center gap-1 mx-1 px-2 py-1 rounded-md text-sm font-medium transition-colors',
                  pathname?.includes('/ethics') ? 'text-primary bg-primary/10' : 'text-gray-500 hover:text-primary hover:bg-gray-100'
                )}
                aria-label={lt`Ethical Questions`}
                title={lt`Ethical Questions`}
              >
                <HelpIcon sx={{ fontSize: 20 }} aria-hidden="true" />
              </Link>
            )}
            <Notification />
            <AccountMenu />
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </div>
      <div className="h-16 w-full" />
    </header>
  )
}
